import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, CalendarCheck, Music2, Construction } from "lucide-react";
import { Link } from "react-router-dom";
import { ContactForm } from "@/components/ContactForm";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import headerImage from "@/assets/header-pai-velho.jpg";

const Index = () => {
  const { t } = useTranslation();
  const [agendaMesUrl, setAgendaMesUrl] = useState<string>("");
  const [giraSemanaUrl, setGiraSemanaUrl] = useState<string>("");

  useEffect(() => {
    fetchGiraImages();
  }, []);

  const fetchGiraImages = async () => {
    try {
      const { data: images } = await supabase
        .from("current_gira_images")
        .select("image_type, image_url");

      if (images) {
        images.forEach((img) => {
          if (img.image_type === "agenda_mes") {
            setAgendaMesUrl(img.image_url);
          } else if (img.image_type === "gira_semana") {
            setGiraSemanaUrl(img.image_url);
          }
        });
      }
    } catch (error) {
      console.error("Error fetching gira images:", error);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ReligiousOrganization",
    "name": "TUPJA - Terreiro Pai Joaquim de Aruanda",
    "description": t('home.seoDescription'),
    "url": window.location.origin,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "316 Goodland St",
      "addressLocality": "Orlando",
      "addressRegion": "FL",
      "postalCode": "32805",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "28.527787",
      "longitude": "-81.367402"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Friday",
      "opens": "19:30",
      "closes": "22:00"
    }
  };

  return (
    <Layout>
      <SEO 
        title={t('home.seoTitle')}
        description={t('home.seoDescription')}
        keywords="umbanda orlando, terreiro orlando florida, pai joaquim de aruanda, giras umbanda, trabalhos espirituais, orixás, umbanda tradicional, espiritualidade orlando"
        structuredData={structuredData}
      />
      {/* Hero Section with Uploaded Image */}
      <section className="relative min-h-[90vh] flex flex-col items-start justify-between overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${headerImage})`,
            backgroundPosition: '50% 25%'
          }}
        />
        {/* Gradiente vertical suave */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-transparent via-40% to-background" />
        {/* Gradientes laterais para suavizar bordas */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent via-50% to-background" />
        {/* Gradiente extra na parte inferior para transição suave */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background via-background/80 to-transparent" />
        
        <div className="relative z-10 w-full px-4 max-w-6xl mx-auto pt-12">
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl text-primary mb-8 leading-tight font-bold text-center text-stroke-dark-brown animate-fade-in">
            {t('home.title')}
          </h1>
        </div>
        
        <div className="relative z-10 w-full px-4 max-w-6xl mx-auto pb-20">
          <div className="max-w-4xl mx-auto bg-white/50 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-2xl border-2 border-white/30 text-center">
            <p className="font-body text-xl md:text-2xl lg:text-3xl text-foreground leading-relaxed italic mb-6">
              {t('home.heroQuote')}
            </p>
            <p className="text-xl md:text-2xl text-secondary font-display font-semibold">
              {t('home.heroAuthor')}
            </p>
          </div>
        </div>
      </section>

      {/* Sobre o Terreiro */}
      <section className="py-20 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl text-primary mb-8 font-bold">
              {t('home.welcomeTitle')}
            </h2>
            <p className="font-body text-xl text-foreground/80 leading-relaxed mb-6">
              {t('home.welcomeText1')}
            </p>
            <p className="font-body text-xl text-foreground/80 leading-relaxed">
              {t('home.welcomeText2')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Agendamento de Consultas */}
      <section className="py-16 bg-gradient-to-br from-logo-brown/10 via-secondary/10 to-logo-green/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden border-2 border-logo-brown/30 shadow-2xl bg-gradient-to-br from-card to-muted/50">
              <CardContent className="p-8 md:p-12">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-logo-brown to-secondary flex items-center justify-center shadow-lg flex-shrink-0">
                    <CalendarCheck className="w-12 h-12 text-white" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="font-display text-3xl md:text-4xl text-primary mb-4 font-bold">
                      {t('home.appointmentTitle')}
                    </h2>
                    <p className="font-body text-lg text-foreground/80 leading-relaxed mb-4">
                      {t('home.appointmentDesc')}
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center md:justify-start mb-6">
                      <span className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                        Seu Zé Pelintra
                      </span>
                      <span className="inline-flex items-center px-4 py-2 bg-secondary/10 text-secondary rounded-full text-sm font-medium">
                        Seu Lúcifer
                      </span>
                    </div>
                    <Link to="/auth">
                      <Button size="lg" className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all">
                        <CalendarCheck className="w-5 h-5 mr-2" />
                        {t('home.appointmentButton')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Ouça Nossos Pontos - Spotify */}
      <section className="py-16 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden border-2 border-logo-brown/30 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:border-logo-brown/50 bg-gradient-to-br from-card to-muted/50">
              <CardContent className="p-8 md:p-12">
                <div className="flex flex-col items-center gap-6 text-center">
                  {/* Ícone de Música */}
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-logo-brown to-secondary flex items-center justify-center shadow-lg">
                    <Music2 className="w-10 h-10 text-white" />
                  </div>
                  
                  {/* Título e Subtítulo Traduzíveis */}
                  <div>
                    <h2 className="font-display text-3xl md:text-4xl text-primary mb-3 font-bold">
                      {t('home.spotifyTitle')}
                    </h2>
                    <p className="font-body text-lg text-muted-foreground">
                      {t('home.spotifySubtitle')}
                    </p>
                  </div>
                  
                  {/* Player Spotify Incorporado */}
                  <div className="w-full max-w-md rounded-xl overflow-hidden shadow-lg">
                    <iframe 
                      src="https://open.spotify.com/embed/playlist/7bUvlhXBCDE4sfLUnifun3?utm_source=generator&theme=0"
                      width="100%" 
                      height="352"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      className="rounded-xl"
                      title="Spotify Player"
                    />
                  </div>
                  
                  {/* Botão para abrir no Spotify */}
                  <a 
                    href="https://open.spotify.com/playlist/7bUvlhXBCDE4sfLUnifun3"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#158a3d] hover:bg-[#1a9e47] text-white font-semibold rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                    </svg>
                    {t('home.spotifyOpenButton')}
                  </a>
                  
                  {/* Nota explicativa */}
                  <p className="text-sm text-muted-foreground italic">
                    {t('home.spotifyNote')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Calendário de Giras */}
      <section className="py-20 bg-gradient-to-b from-card to-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-sacred flex items-center justify-center shadow-lg glow-sacred">
                <Calendar className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-primary mb-4 font-bold">
                {t('home.calendarTitle')}
              </h2>
              <p className="font-body text-xl text-muted-foreground">
                {t('home.calendarSubtitle')}
              </p>
            </div>

            <div className="max-w-2xl mx-auto mb-12">
              <Card className="overflow-hidden border-2 border-primary/30 shadow-2xl rounded-xl bg-gradient-to-br from-card to-muted/50">
                <CardContent className="p-8 md:p-12">
                  <div className="flex flex-col items-center gap-6 text-center">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center shadow-lg animate-pulse">
                      <Construction className="w-10 h-10 text-primary" />
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl text-primary font-bold">
                      Em Desenvolvimento Interno
                    </h3>
                    <p className="font-body text-lg text-foreground/80 leading-relaxed max-w-lg">
                      Estamos em período de desenvolvimento interno. Em breve retornaremos com nossos atendimentos e giras. Aguardem novidades!
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Como Chegar */}
      <section className="py-8 bg-gradient-to-b from-muted/20 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-sacred flex items-center justify-center shadow-lg glow-sacred">
                <MapPin className="w-10 h-10 text-primary" />
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-primary mb-4 font-bold">
                {t('home.howToGetThere')}
              </h2>
              <p className="font-body text-xl text-muted-foreground">
                {t('home.visitUs')}
              </p>
            </div>

            <Card className="overflow-hidden border-2 border-primary/20 shadow-2xl">
              <CardContent className="p-0">
                <div className="relative w-full h-[400px] md:h-[500px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3505.0739287344694!2d-81.36967632395051!3d28.52778778885892!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e77f7c8c8c8c8d%3A0x8c8c8c8c8c8c8c8c!2s316%20Goodland%20St%2C%20Orlando%2C%20FL%2032805!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={t('home.howToGetThere')}
                  />
                </div>
                <div className="p-6 md:p-8 bg-gradient-to-br from-primary/5 to-secondary/5">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-display text-xl md:text-2xl text-primary font-bold mb-2">
                        316 Goodland St
                      </p>
                      <p className="font-body text-lg text-foreground/80">
                        Orlando, FL 32805
                      </p>
                      <p className="font-body text-base text-muted-foreground mt-4">
                        {t('home.locationInfo')}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Fale Conosco */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-display text-4xl md:text-5xl text-center text-primary mb-6 font-bold">
              {t('home.contactUs')}
            </h2>
            <p className="text-center text-muted-foreground mb-12 text-xl font-body">
              {t('home.contactSubtitle')}
            </p>
            <div className="bg-gradient-to-br from-muted/30 to-background p-8 rounded-2xl border-2 border-secondary/20 shadow-xl">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Final Section */}
      <section className="relative py-32 overflow-hidden bg-logo-gray">
        <div className="relative z-10 text-center px-4">
          <p className="font-display text-6xl md:text-7xl text-logo-beige drop-shadow-2xl font-bold mb-4">
            {t('common.axe')}
          </p>
          <p className="font-body text-xl md:text-2xl text-white/90 italic">
            {t('home.finalMessage')}
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
